import type { PackingSlipItem, Page } from './types';
import {
  ITEMS_PAGE_1_MULTI,
  ITEMS_PAGE_1_SINGLE,
  ITEMS_MIDDLE_PAGE,
  ITEMS_LAST_PAGE,
} from './constants';

export function paginateItems(items: PackingSlipItem[]): Page[] {
  const total = items.length;

  // Single page: all items fit with notice + instructions
  if (total <= ITEMS_PAGE_1_SINGLE) {
    return [
      {
        items,
        isFirstPage: true,
        isLastPage: true,
        hasMore: false,
      },
    ];
  }

  const pages: Page[] = [];
  let cursor = 0;

  // Page 1: associate notice eats space → fewer items, NO instructions
  const page1Count = Math.min(ITEMS_PAGE_1_MULTI, total);
  pages.push({
    items: items.slice(cursor, cursor + page1Count),
    isFirstPage: true,
    isLastPage: false,
    hasMore: page1Count < total,
  });
  cursor += page1Count;

  // Middle + last pages
  while (cursor < total) {
    const remaining = total - cursor;

    if (remaining <= ITEMS_LAST_PAGE) {
      // Everything left fits on a last page with instructions
      pages.push({
        items: items.slice(cursor),
        isFirstPage: false,
        isLastPage: true,
        hasMore: false,
      });
      cursor = total;
    } else {
      // Fill this middle page to capacity; whatever is left goes to
      // subsequent pages (middle or last).
      const count = Math.min(ITEMS_MIDDLE_PAGE, remaining);
      pages.push({
        items: items.slice(cursor, cursor + count),
        isFirstPage: false,
        isLastPage: false,
        hasMore: true,
      });
      cursor += count;
    }
  }

  // If all items fit on the first page using the multi-page capacity
  // (i.e., total is between ITEMS_PAGE_1_SINGLE + 1 and ITEMS_PAGE_1_MULTI,
  // currently 12–24 items), the while loop never runs. Page 1 doesn't have
  // room for instructions alongside the associate notice (capacity with both
  // = ITEMS_PAGE_1_SINGLE), so add a separate instructions-only last page.
  if (pages.length === 1) {
    pages[0].hasMore = false;
    pages.push({
      items: [],
      isFirstPage: false,
      isLastPage: true,
      hasMore: false,
    });
  }

  // Ensure last page is marked correctly
  pages[pages.length - 1].isLastPage = true;
  pages[pages.length - 1].hasMore = false;

  return pages;
}
